import React from "react";
import {
  MapPin,
  Building2,
  Clock,
  DollarSign,
  Calendar,
  Info,
  Pencil,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import JobPreferencesForm from "@/components/Forms/JobPreferencesForm";
import { JobSearchStatus } from "@api/shared-runtime/enums";

interface JobPreferencesCardProps {
  preferredLocations?: string[];
  practiceSettings?: { id: string; displayText: string }[];
  employmentTypes?: { id: string; displayText: string }[];
  minHourlyRate?: string | null;
  availability?: string | null;
  noPreferences?: boolean;
  editable?: boolean;
  isEditing?: boolean;
  onEditChange?: (editing: boolean) => void;
  userId?: string;
  searchStatus?: JobSearchStatus;
  onCancel?: () => void;
}

export const JobPreferencesCard = ({
  searchStatus,
  preferredLocations = [],
  practiceSettings = [],
  employmentTypes = [],
  minHourlyRate,
  availability,
  noPreferences = false,
  editable = false,
  isEditing = false,
  userId,
  onEditChange,
}: JobPreferencesCardProps) => {
  if (searchStatus === JobSearchStatus.NotLooking && !isEditing) {
    return (
      <Card className="w-full bg-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800">
              Job Preferences
            </CardTitle>
            {editable && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEditChange?.(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Info className="w-10 h-10 text-gray-400 mb-3" />
            <p className="text-gray-600 mb-1">
              This provider is not currently looking for a job
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (noPreferences) {
    return (
      <Card className="w-full bg-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-gray-800">
            Job Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Info className="w-10 h-10 text-gray-400 mb-3" />
            <p className="text-gray-600 mb-1">
              This provider has not filled out their job preferences
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasAnyCity = preferredLocations.includes("ANY_CITY");
  const displayLocations = preferredLocations.filter(
    (loc) => loc !== "ANY_CITY",
  );
  const onlyAnyCity = hasAnyCity && displayLocations.length === 0;

  return (
    <Card
      className={cn("w-full bg-white", {
        "shadow-none border-none": isEditing,
      })}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-800">
            Job Preferences
          </CardTitle>
          {isEditing || !editable ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditChange?.(true)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4">
        {isEditing ? (
          <JobPreferencesForm
            userId={userId}
            onComplete={() => onEditChange?.(false)}
            onCancel={() => onEditChange?.(false)}
          />
        ) : searchStatus === JobSearchStatus.NotLooking ? (
          <div className="col-span-2 xl:col-span-1 flex flex-col items-center justify-center py-6 text-center">
            <Info className="w-10 h-10 text-gray-400 mb-3" />
            <p className="text-gray-600 mb-1">
              This provider is not currently looking for a job
            </p>
          </div>
        ) : (
          <>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-gray-400" />
                <h3 className="text-gray-700 font-medium">Locations</h3>
              </div>
              <div className="pl-6 text-gray-600 flex flex-col gap-1">
                {onlyAnyCity || preferredLocations.length === 0 ? (
                  <div>Any location</div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1">
                      {displayLocations.map((location, index) => (
                        <React.Fragment key={location}>
                          <span>{location}</span>
                          {index < displayLocations.length - 1 && (
                            <span>•</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    {hasAnyCity && (
                      <div className="text-gray-500 text-sm italic">
                        Also open to other locations
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-gray-400" />
                <h3 className="text-gray-700 font-medium">Practice Settings</h3>
              </div>
              <div className="pl-6 text-gray-600 flex flex-wrap gap-1">
                {practiceSettings.length === 0 ? (
                  <span>Any practice setting</span>
                ) : (
                  practiceSettings.map((setting, index) => (
                    <React.Fragment key={setting.id}>
                      <span>{setting.displayText}</span>
                      {index < practiceSettings.length - 1 && <span>•</span>}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-gray-400" />
                <h3 className="text-gray-700 font-medium">Employment Types</h3>
              </div>
              <div className="pl-6 text-gray-600 flex flex-wrap gap-1">
                {employmentTypes.length === 0 ? (
                  <span>Any employment type</span>
                ) : (
                  employmentTypes.map((type, index) => (
                    <React.Fragment key={type.id}>
                      <span>{type.displayText}</span>
                      {index < employmentTypes.length - 1 && <span>•</span>}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>

            {/* Only show minimum rate to the provider themselves (editable) or if they have set one */}
            {(editable || minHourlyRate) && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <h3 className="text-gray-700 font-medium">Minimum Rate</h3>
                </div>
                <div className="pl-6 text-gray-600">
                  {minHourlyRate ? `$${minHourlyRate} / hour` : "No minimum"}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-gray-400" />
                <h3 className="text-gray-700 font-medium">Availability</h3>
              </div>
              <div className="pl-6 text-gray-600">
                {availability || "Flexible"}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
